/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { FacetMobilePaneProvider } from '@/components/category-refinements/drilldown';

/**
 * NM's MOBILE filter control: live NM's left-anchored `FILTER BY:` takeover,
 * opened by the `Filters` segment of the mobile sticky bar.
 *
 * WHAT THIS REPLACES, AND WHY IT IS A MECHANISM CHANGE RATHER THAN A RESTYLE.
 * Until now ONE element served both the desktop facet rail and the mobile filter
 * panel, and only its WIDTH was gated: `w-full md:w-(--srp-rail-width)` with a
 * `hidden md:block` arm that was dropped the moment the panel opened. So at
 * mobile the "overlay" was the rail, `position: static`, in normal flow, sitting
 * BELOW the site header and the results heading with both still painted and
 * still hit-testable above it. Measured at `clientWidth` 390 before this landed:
 * `#search-filters-panel` at `y = 565.8` on NM, `position: static`, no scroll
 * lock, and an `elementFromPoint` at the header's own coordinates landing in the
 * header. That is the owner's report exactly — *"ours leave the header and
 * search results header visible above the filters overlay"* — and no amount of
 * CSS on that element could have fixed it while it remained in flow.
 *
 * A `position: fixed` RULE ON THE SAME ELEMENT WAS THE OTHER OPTION AND WAS
 * REJECTED. It would have covered the screen and it cannot supply a focus trap,
 * Escape-to-close, focus restoration, or background inertness — four things a
 * shopper on a phone needs and none of which are visible in a screenshot. The
 * pane is therefore a real dialog.
 *
 * THE EVIDENCE, and how each number was obtained. Live NM serves a desktop
 * response to anything but a real handset, so this panel cannot be emulated or
 * decompiled; the source is a PHOTOGRAPH of the owner's own iPhone, address bar
 * reading `neimanmarcus.com` —
 * `.reference/owner-nm-mobile-2026-08-23/f1454eec-…jpeg`. Decoded and scanned
 * rather than eyeballed; the full table is on the `--srp-mobile-filter-*` token
 * block in `theme/tokens/brand-nm/search.css`. The two numbers worth repeating
 * here:
 *
 *   panel width   leading white run x 0..705 of 942 = 74.95%, identical at
 *                 three rows inside the panel's blank lower band, running the
 *                 FULL viewport height (y 241..1878).
 *   row pitch     109.1 device px = 45.2 CSS across nine group rows, above a
 *                 44.3 CSS count row and a 47.2 CSS header band.
 *
 * THE WIDTH IS NOT THE SORT PANEL'S WIDTH, and that is the one value that had to
 * be measured rather than inherited from the panel beside it: the same device on
 * the same day gives the SORT panel 88.64% and this one 74.95%. A shared token
 * would have been wrong for one of the two. Both are expressed as percentages
 * because a percentage is what was measured and it degrades sanely at the other
 * mobile widths, where nothing was measured.
 *
 * THE CSS-PIXEL VALUES CARRY ±1%. A photograph does not record its own device,
 * so the scale factor comes from an ASSUMED 390 CSS viewport (942/390 = 2.4154
 * px/CSS). The RATIOS carry no such assumption and are exact.
 *
 * THE GROUPS ARE THE RAIL'S OWN, DELIBERATELY. This pane renders the same
 * `CategoryRefinements` the desktop rail does, with the same props, because live
 * NM's mobile panel is the same nine groups in the same order — Category, Get it
 * Fast, Gender, Designer, Size, Color, Price, Availability, Sale — expanding IN
 * PLACE behind a `+`/`−`, which is what that component already draws. Forking a
 * mobile-only list would have created a second source of truth for refinement
 * state and would have drifted the moment either changed.
 *
 * THE ROW RHYTHM ARRIVES AS SCOPED TOKENS, NOT AS NEW RULES. The pane
 * redeclares `--facet-group-header-height`, `--facet-divider`,
 * `--facet-item-font-size` and `--facet-rail-pad-x` ON ITSELF, so the `rail`
 * variant paints live's mobile rhythm here and the DESKTOP rail keeps the values
 * it was separately measured with. Nothing lands in a shared `:root`, so no
 * brand and no surface inherits a number nobody measured for it.
 *
 * `DONE`, NOT AN X, and it is at the TRAILING edge. That is the opposite of BG,
 * whose pane closes with an X at the LEADING edge, and it is measured on both
 * sides rather than assumed from one. NM's is a filled black rectangle,
 * 45.5 x 19.5 CSS, its right edge 9.1 CSS in from the panel's.
 *
 * ACCESSIBILITY COMES FROM `Sheet`, NOT FROM HAND-ROLLED CODE — this repo's
 * Radix dialog wrapper, already carrying the focus trap, Escape-to-close, the
 * scroll lock, and `aria-hidden` on everything behind the portal.
 *
 * IT DOES NOT SET `aria-modal`, which is checked rather than assumed: the
 * rendered attributes are `role="dialog"`, `aria-labelledby` and `data-state`,
 * and no `aria-modal`. Radix hides the background instead — every body child but
 * the portal gains `aria-hidden="true"` — which removes them from the
 * accessibility tree outright rather than merely advising against them. The
 * guarantee is real; a test asserting `aria-modal` would redden against a
 * correct implementation. Assert the hidden siblings.
 *
 * FOCUS IS RESTORED EXPLICITLY, NOT LEFT TO RADIX. Radix returns focus to
 * whatever held it before the pane opened — which is nothing here. On macOS and
 * on touch, clicking a `<button>` does not focus it, so at the moment of opening
 * `document.activeElement` is the body and there is nothing to restore TO. The
 * trigger's ref is therefore threaded in from the route and focused by hand in
 * `onCloseAutoFocus`, which is correct on every platform rather than only on the
 * ones where a click happens to focus.
 *
 * NM ONLY BY MOUNT, which is why nothing here is brand-keyed. The routes mount
 * this behind `SearchUx.mobileStickyBar` / the PLP's equivalent — true for NM,
 * false for BG. BG's mobile filter pane is a DIFFERENT BAND: full-viewport, a
 * centred `FILTERS` with no colon, an X at the leading edge, no item-count row,
 * and eight groups rather than nine. It lives in `bg-mobile-filter-overlay.tsx`.
 * These are two builds, not one component with a brand flag.
 */
export default function NmMobileFilterOverlay({
    open,
    onOpenChange,
    total,
    panelId,
    triggerRef,
    children,
}: {
    open: boolean;
    onOpenChange: (next: boolean) => void;
    /** The query's own result count — live puts `11,250 Items` in its own row. */
    total: number;
    /**
     * The id the mobile trigger's `aria-controls` points at. It lands on the
     * PANE rather than on the desktop rail, because the pane is what the mobile
     * control actually discloses; the rail is `hidden md:block` and discloses
     * nothing at this width. While the pane is closed the id is absent, which is
     * the standard disclosure shape and which axe permits precisely because the
     * trigger carries `aria-expanded="false"` alongside it.
     */
    panelId: string;
    triggerRef: RefObject<HTMLButtonElement | null>;
    /** The rail's own `CategoryRefinements`, composed at the mount site. */
    children: ReactNode;
}): ReactElement {
    const { t } = useTranslation('search');

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="left"
                id={panelId}
                data-testid="nm-mobile-filter-overlay"
                /* BOTH max-width classes are load-bearing, and the responsive one
                   is the whole point. `SheetContent`'s `side="left"` arm ends in
                   `sm:max-w-sm`, which caps the pane at 384px from 640 up —
                   inside the mobile tier this overlay serves, since `md` is 768.
                   So between 640 and 767 the measured 75% would silently become
                   a 384px column. `max-w-none` ALONE DOES NOT FIX IT:
                   tailwind-merge treats a base utility and its `sm:` variant as
                   different properties, so both survive into the class list and
                   the variant still wins above 640. The `sm:` arm has to be
                   cancelled by its own `sm:` arm.

                   `md:hidden` is the belt-and-braces guard for a resize that
                   happens while the pane is open — the control that opens it is
                   already `md:hidden`, so this is not the gate, and it is a
                   Tailwind responsive class rather than a JS width branch. */
                className="srp-mobile-filter-panel md:hidden flex w-(--srp-mobile-filter-panel-width) max-w-none flex-col gap-0 border-0 p-0 sm:max-w-none [--ui-shadow:none]"
                overlayClassName="srp-mobile-filter-scrim md:hidden"
                /* Radix auto-wires `aria-describedby` at an id it then expects a
                   `Dialog.Description` to claim. This pane has no description —
                   its title, its count row and its groups are the whole content
                   — so the opt-out is explicit. Left implicit it ships a dangling
                   IDREF, which resolves to nothing and which Radix warns about at
                   runtime. */
                aria-describedby={undefined}
                onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    triggerRef.current?.focus();
                }}
                closeButton={null}>
                <div className="srp-mobile-filter-header flex shrink-0 items-center justify-between">
                    <SheetTitle className="srp-mobile-filter-title">
                        {t('filters.panelTitle', { defaultValue: 'Filter By:' })}
                    </SheetTitle>
                    <button
                        type="button"
                        data-testid="nm-mobile-filter-done"
                        onClick={() => onOpenChange(false)}
                        className="focus-ring-button srp-mobile-filter-done inline-flex cursor-pointer items-center justify-center">
                        {t('filters.done', { defaultValue: 'Done' })}
                    </button>
                </div>

                {/* Live's own row, and BG has no counterpart for it. `itemsCount`
                    is the number-formatted variant — the photograph reads
                    `11,250 Items` WITH the separator, which is what that key
                    produces and which `itemsCountSearch` (unseparated) does not. */}
                <p data-testid="nm-mobile-filter-count" className="srp-mobile-filter-count flex shrink-0 items-center">
                    {t('stickyBar.itemsCount', { count: total, defaultValue: `${total} Items` })}
                </p>

                {/* THE PROVIDER MARKS THIS AS A PANE, WHICH IS A DIFFERENT
                    SURFACE FROM THE DESKTOP RAIL — not a different brand.

                    It carries `collapseAllByDefault`, so the rail inside opens
                    NO group on first paint. Live's desktop rails open Category
                    on both brands; live's mobile panes open nothing. Reported by
                    the owner, 2026-08-23: *"live opens with no filter options
                    open, we have categories open. same on bg actually."*
                    Corroborated by his own handset photograph of live NM
                    (`neimanmarcus.com` legible in frame), which shows Category
                    carrying a minus only AFTER he tapped it.

                    It wraps only the `children`, not the header — the count row
                    and the DONE control have nothing to do with facet state. */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    <FacetMobilePaneProvider>{children}</FacetMobilePaneProvider>
                </div>
            </SheetContent>
        </Sheet>
    );
}
